import type { ComponentPropsWithRef, ReactNode } from "react";
import { forwardRef } from "react";
import { t } from "@/i18n-js/instance";
import { BlockSection } from "@/react/components/shared/BuilderUI/BlockSection";
import { Button } from "@/react/components/shared/uikit/Button";
import { Form } from "@/react/components/shared/uikit/Form";
import { useTopBlockStyles } from "../../../Hooks/useTopBlockStyles";
import type { StyleValue } from "../../../StyleValue.types";
import { defaultPrimaryStyles } from "../../Content/Button/defaultPrimaryStyles";
import { secondaryBlockDefaultStyles } from "../../Homepage/constants";

export interface ContactBlockProps extends ComponentPropsWithRef<"div"> {
  footer: ReactNode;
  header: ReactNode;
  rightContent: ReactNode;
  styles?: Record<string, StyleValue>;
}

export const ContactBlock = forwardRef<HTMLDivElement, ContactBlockProps>(
  ({ header, footer, rightContent, className, styles, ...rest }, ref) => {
    const { backgroundStyles, paddingStyles, marginStyles } = useTopBlockStyles(
      {
        styles,
        defaultStyles: secondaryBlockDefaultStyles,
      },
    );

    return (
      <BlockSection ref={ref} className={className} {...rest}>
        <BlockSection.Background style={backgroundStyles}>
          <BlockSection.Container>
            <BlockSection.Content
              style={{
                ...paddingStyles,
                ...marginStyles,
              }}
            >
              <div className="wb-flex wb-flex-col md:wb-grid md:wb-grid-cols-2 wb-gap-10 md:wb-gap-20">
                <div className="wb-flex wb-flex-col wb-justify-between md:wb-py-12 wb-gap-14 md:wb-gap-28">
                  <BlockSection.NestedBlock className="wb-flex wb-flex-col wb-gap-6">
                    {header}
                  </BlockSection.NestedBlock>
                  <BlockSection.NestedBlock className="wb-flex wb-flex-col wb-gap-4 wb-py-2">
                    {footer}
                  </BlockSection.NestedBlock>
                </div>
                <div className="wb-flex wb-flex-col">
                  {rightContent}
                  {/* TODO: Implement */}
                  <Form
                    onSubmit={() => {}}
                    formClassName="wb-flex wb-flex-col wb-gap-5 wb-bg-secondary wb-rounded-2xl wb-px-6 wb-py-5 md:wb-p-8"
                  >
                    <Form.Item
                      name="name"
                      hideDescription
                      label={t("builder_ui.blocks.contact.name")}
                      className="wb-flex wb-flex-col wb-gap-1.5"
                    >
                      <Form.Input
                        type="text"
                        name="name"
                        className="wb-w-full wb-rounded-lg wb-border-none wb-px-4 wb-py-3"
                      />
                    </Form.Item>
                    <Form.Item
                      name="email"
                      hideDescription
                      label={t("builder_ui.blocks.contact.email")}
                      className="wb-flex wb-flex-col wb-gap-1.5"
                    >
                      <Form.Input
                        type="email"
                        name="email"
                        className="wb-w-full wb-rounded-lg wb-border-none wb-px-4 wb-py-3"
                      />
                    </Form.Item>
                    <Form.Item
                      name="message"
                      hideDescription
                      label={t("builder_ui.blocks.contact.message")}
                      placeholder={t(
                        "builder_ui.blocks.contact.message_placeholder",
                      )}
                      className="wb-flex wb-flex-col wb-gap-1.5"
                    >
                      <Form.TextArea
                        name="message"
                        rows={6}
                        className="wb-w-full wb-rounded-lg wb-border-none wb-px-4 wb-py-3 placeholder:wb-text-[var(--theme-website-color-3)]"
                      />
                    </Form.Item>
                    <Form.Item
                      name="accept_terms"
                      hideDescription
                      hideLabel
                      className="wb-flex wb-flex-col wb-gap-1.5"
                    >
                      <Form.ToggleSwitch
                        name="accept_terms"
                        variant="small"
                        label={t(
                          "builder_ui.blocks.contact.terms_and_conditions",
                        )}
                        className="wb-rounded wb-border wb-border-primary wb-size-5"
                        wrapperClass="wb-flex wb-items-center wb-gap-2"
                      />
                    </Form.Item>
                    <div className="wb-mt-4">
                      <Button
                        type="submit"
                        className="wb-rounded-full wb-px-7 wb-py-3 wb-font-semibold"
                        style={{
                          backgroundColor:
                            defaultPrimaryStyles.default.backgroundColor,
                          color: defaultPrimaryStyles.default.color,
                        }}
                      >
                        {t("builder_ui.blocks.contact.button_text")}
                      </Button>
                    </div>
                  </Form>
                </div>
              </div>
            </BlockSection.Content>
          </BlockSection.Container>
        </BlockSection.Background>
      </BlockSection>
    );
  },
);

ContactBlock.displayName = "ContactBlock";
